import { useMemo } from "react";
import { useSelector } from "react-redux";

import { useGetVisitorsQuery } from "../slices/visitorsSlice";

// Everything here reads the cached member list, so the only network call is
// the enquiry one — and because RTK Query dedupes identical queries, the
// header badge, the bottom menu and the reminders page share one request.

// India has no DST, so a fixed offset converts any instant to an IST calendar
// day exactly. Deliberately avoids toLocaleDateString with a timeZone/locale:
// the Android System WebView and older iOS WKWebView builds do not all ship
// full ICU data, and a locale fallback there would silently reformat the date
// and break every comparison below.
const IST_OFFSET_MS = 5 * 60 * 60 * 1000 + 30 * 60 * 1000;

// -> "YYYY-MM-DD" for the IST calendar day, or "" when unparseable.
export const istDayKey = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Date(date.getTime() + IST_OFFSET_MS).toISOString().slice(0, 10);
};

// "MM-DD", used to match birthdays regardless of year.
const monthDay = (dayKey) => dayKey.slice(5);

export const useReminders = () => {
  const { userInfo } = useSelector((state) => state.auth);

  const today = istDayKey(new Date());

  const { data: visitors, isFetching: enquiriesLoading } = useGetVisitorsQuery(
    { query: `reminderDate=${today}`, token: userInfo?.token },
    { skip: !userInfo?.token }
  );

  return useMemo(() => {
    const members = userInfo?.allUsers || [];
    const todayMonthDay = monthDay(today);

    const expiringToday = [];
    const pendingDueToday = [];
    const birthdays = [];

    members.forEach((member) => {
      // Validity ending today.
      if (istDayKey(member?.subscriptionExpiryDate) === today) {
        expiringToday.push(member);
      }

      // A due date reached today, and money actually outstanding.
      if (
        (member?.pendingAmount || 0) > 0 &&
        istDayKey(member?.pendingDueDate) === today
      ) {
        pendingDueToday.push(member);
      }

      const dob = istDayKey(member?.dateOfBirth);
      if (dob && monthDay(dob) === todayMonthDay) {
        birthdays.push(member);
      }
    });

    // Incoming WhatsApp messages the gym has not opened yet.
    const unreadMessages = (userInfo?.wsMsgs || []).filter(
      (msg) =>
        msg?.messageKind?.trim() === "In_Coming" &&
        msg?.messageResponse?.toLowerCase() !== "read"
    );

    const enquiries = Array.isArray(visitors) ? visitors : [];

    return {
      expiringToday,
      pendingDueToday,
      enquiries,
      birthdays,
      unreadMessages,
      enquiriesLoading,
      totalCount:
        expiringToday.length +
        pendingDueToday.length +
        enquiries.length +
        birthdays.length +
        unreadMessages.length,
    };
  }, [userInfo?.allUsers, userInfo?.wsMsgs, visitors, enquiriesLoading, today]);
};

export default useReminders;
